import React, { useEffect, useRef, useState } from 'react'
import { HiOutlineTicket, HiOutlineGift, HiChevronDown, HiCheckCircle, HiOutlineInformationCircle } from 'react-icons/hi'
import CartService from '../service/CartService'
import { formatRupee } from './Price'
import { couponValue, couponTerms } from '../util/coupons'

// The coupon part of the order summary (cart and checkout). `coupon` = cartCoupon(GET /cart); every change returns
// the new cart, passed to onCart(data) so the totals update. The best automatic coupon is already applied by the
// server; the shopper can remove it, type a code, or pick another of their offers.
const CouponBox = ({ coupon, onCart, disabled }) => {
    const [open, setOpen] = useState(false)
    const [code, setCode] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [offers, setOffers] = useState([])
    const [showOffers, setShowOffers] = useState(false)
    const [pop, setPop] = useState(false)
    const inputRef = useRef(null)
    const applied = coupon && coupon.code

    const loadOffers = () => {
        CartService.getCoupons().then(res => setOffers(res.data.data || [])).catch(() => setOffers([]))
    }

    useEffect(() => {
        loadOffers()
    }, [applied])

    useEffect(() => {
        if (open && inputRef.current) inputRef.current.focus()
    }, [open])

    const done = (res, celebrate) => {
        setBusy(false)
        setError('')
        setCode('')
        setOpen(false)
        if (celebrate) {
            setPop(true)
            window.setTimeout(() => setPop(false), 700)
        }
        onCart(res.data.data)
    }

    const apply = (value) => {
        const text = String(value || '').trim()
        if (!text) {
            setError('Please enter a coupon code.')
            return
        }
        setBusy(true)
        setError('')
        CartService.applyCoupon(text).then(res => done(res, true)).catch(err => {
            setBusy(false)
            setError(err?.response?.data?.msg || 'We could not apply this code. Please try again.')
        })
    }

    const remove = () => {
        setBusy(true)
        CartService.removeCoupon().then(res => done(res, false)).catch(err => {
            setBusy(false)
            setError(err?.response?.data?.msg || 'We could not remove the coupon. Please try again.')
        })
    }

    const otherOffers = offers.filter(o => !o.applied)

    return (
        <div className='mv-coupon-box'>
            {applied ?
                <div className={pop ? 'mv-coupon-applied is-pop' : 'mv-coupon-applied'} role='status'>
                    <span className='mv-coupon-applied-icon' aria-hidden='true'><HiCheckCircle /></span>
                    <span className='mv-coupon-applied-text'>
                        <span className='mv-coupon-applied-title'>
                            <span className='mv-coupon-code'>{coupon.code}</span> applied
                        </span>
                        <span className='mv-coupon-applied-sub'>
                            {coupon.discount > 0
                                ? <>You save {formatRupee(coupon.discount)}{coupon.itemName ? ` on ${coupon.itemName}` : ''}{coupon.freeDelivery ? ' + free delivery' : ''}</>
                                : 'Free delivery on this order'}
                            {coupon.auto ? ' · best offer, added for you' : ''}
                        </span>
                    </span>
                    <button type='button' className='mv-link-btn mv-coupon-remove' onClick={remove} disabled={busy || disabled}>
                        {busy ? 'Removing…' : 'Remove'}
                    </button>
                </div> :
                <div className='mv-coupon-entry'>
                    <button type='button' className='mv-coupon-toggle' aria-expanded={open} aria-controls='mv-coupon-form'
                        onClick={() => { setOpen(o => !o); setError('') }} disabled={disabled}>
                        <HiOutlineTicket aria-hidden='true' />
                        <span>Have a coupon code?</span>
                        <HiChevronDown className='mv-coupon-chevron' aria-hidden='true' />
                    </button>
                    {open &&
                        <form id='mv-coupon-form' className='mv-coupon-form' noValidate
                            onSubmit={(e) => { e.preventDefault(); apply(code) }}>
                            <label htmlFor='mv-coupon-input' className='mv-visually-hidden'>Coupon code</label>
                            <input id='mv-coupon-input' ref={inputRef} className='mv-input mv-coupon-input' value={code}
                                onChange={(e) => { setCode(e.target.value.toUpperCase()); setError('') }}
                                placeholder='Enter code' autoComplete='off' autoCapitalize='characters' spellCheck='false'
                                maxLength={30} aria-invalid={error ? 'true' : undefined}
                                aria-describedby={error ? 'mv-coupon-error' : undefined} />
                            <button type='submit' className='mv-btn mv-btn-secondary mv-btn-sm' disabled={busy}>
                                {busy ? 'Applying…' : 'Apply'}
                            </button>
                        </form>}
                </div>}

            {error && <p id='mv-coupon-error' className='mv-field-error mv-coupon-error' role='alert'>{error}</p>}
            {!error && coupon && coupon.error &&
                <p className='mv-coupon-note'><HiOutlineInformationCircle aria-hidden='true' /> {coupon.error}</p>}
            {coupon && coupon.message &&
                <p className='mv-coupon-nudge'><HiOutlineGift aria-hidden='true' /> {coupon.message}</p>}

            {otherOffers.length > 0 &&
                <div className='mv-coupon-offers'>
                    <button type='button' className='mv-link-btn mv-coupon-offers-toggle' aria-expanded={showOffers}
                        onClick={() => setShowOffers(s => !s)}>
                        {showOffers ? 'Hide offers' : `${otherOffers.length} more ${otherOffers.length === 1 ? 'offer' : 'offers'} for you`}
                    </button>
                    {showOffers &&
                        <ul className='mv-coupon-offer-list'>
                            {otherOffers.map(o =>
                                <li key={o.code} className='mv-coupon-offer'>
                                    <span className='mv-coupon-offer-value'>{couponValue(o)}</span>
                                    <span className='mv-coupon-offer-text'>
                                        <span className='mv-coupon-offer-name'>{o.name}</span>
                                        <span className='mv-coupon-offer-terms'>
                                            {o.discount != null ? `Saves ${formatRupee(o.discount)} · ` : ''}{couponTerms(o)}
                                        </span>
                                        {o.shortBy != null &&
                                            <span className='mv-coupon-offer-short'>Add {formatRupee(o.shortBy)} more to use it</span>}
                                    </span>
                                    <button type='button' className='mv-btn mv-btn-secondary mv-btn-sm'
                                        disabled={busy || disabled || o.discount == null} onClick={() => apply(o.code)}>
                                        Apply
                                    </button>
                                </li>
                            )}
                        </ul>}
                </div>}
        </div>
    )
}

export default CouponBox
